import { type FC, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { SeoLink } from 'dibs-elements/exports/SeoLink.tsx';
import { trackEvent } from 'dibs-tracking';
import { ProductDimensions } from './ProductDimensions.tsx';
import { PipeSeparator } from './PipeSeparator.tsx';

import { useSponsoredLabel } from 'dibs-sponsored-ads/exports/useSponsoredLabel.ts';

import { type ProductDetails_item$key } from './__generated__/ProductDetails_item.graphql.ts';
import { type ProductDetails_user$key } from './__generated__/ProductDetails_user.graphql.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

const messages = {
    offeredBy: (sellerName: string): ReactElement => (
        <FormattedMessage
            id="dc.buyerProductTile.offeredBy"
            defaultMessage={`Offered by {sellerName}`}
            values={{ sellerName }}
        />
    ),
};

const itemFragment = graphql`
    fragment ProductDetails_item on Item @argumentDefinitions(showSeller: { type: "Boolean!" }) {
        serviceId
        seller @include(if: $showSeller) {
            sellerProfile {
                company
            }
        }
    }
`;

const userFragment = graphql`
    fragment ProductDetails_user on User @argumentDefinitions(showSeller: { type: "Boolean!" }) {
        canShowSellerName @include(if: $showSeller)
    }
`;

type Props = {
    item: ProductDetails_item$key;
    user: ProductDetails_user$key | null | undefined;
    afterDisplay: boolean;
    isSponsored?: boolean;
    showBrowseSimilar?: boolean;
    showMeasurements?: boolean;
    canShowDimensions?: boolean;
    dimensionValues: string;
};

export const ProductDetails: FC<Props> = ({
    item: itemRef,
    user: userRef,
    afterDisplay,
    isSponsored,
    showBrowseSimilar,
    canShowDimensions,
    dimensionValues,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const sponsoredLabel = useSponsoredLabel({ isClient: afterDisplay });
    const { serviceId } = item || {};
    const { canShowSellerName } = user || {};
    const sellerName = item?.seller?.sellerProfile?.company;
    const showSellerName = afterDisplay && canShowSellerName && sellerName;
    const showSponsoredLabel = isSponsored && sponsoredLabel;

    if (!(showSellerName || canShowDimensions || showBrowseSimilar || showSponsoredLabel)) {
        return null;
    }

    return (
        <p
            className={classnames(
                dibsCss.textBodyXSmallNormal,
                dibsCss.wFull,
                dibsCss.h18px,
                dibsCss.mtXxsmall,
                dibsCss.flex,
                dibsCss.flexRow,
                dibsCss.itemsCenter
            )}
        >
            <span
                className={classnames(
                    dibsCss.textSatan,
                    dibsCss.wFull,
                    dibsCss.flex,
                    dibsCss.minW0,
                    { [styles.hiddenOnHover]: canShowDimensions || showBrowseSimilar }
                )}
            >
                {showSellerName && (
                    <span
                        data-tn="portfolio-row-dealer"
                        className={classnames(
                            dibsCss.truncate,
                            dibsCss.flexShrink,
                            dibsCss.contentCenter
                        )}
                    >
                        {messages.offeredBy(sellerName)}
                    </span>
                )}
                {showSellerName && showSponsoredLabel && <PipeSeparator />}
                {showSponsoredLabel && (
                    <span className={dibsCss.contentCenter}>{sponsoredLabel}</span>
                )}
            </span>
            {canShowDimensions && (
                <ProductDimensions
                    className={classnames(dibsCss.hidden, styles.visibleInlineOnHover)}
                    dimensionValues={dimensionValues}
                />
            )}
            {showBrowseSimilar && (
                <SeoLink
                    className={classnames(
                        dibsCss.textBodyXSmallHeavy,
                        dibsCss.textNoir,
                        dibsCss.flexNowrap,
                        dibsCss.shrink0,
                        dibsCss.hidden,
                        styles.visibleInlineOnHover,
                        canShowDimensions && [dibsCss.mlAuto, dibsCss.plSmall]
                    )}
                    dataTn="browse-similar"
                    linkData={{
                        path: `/similar/?item=${serviceId}`,
                        isLinkable: false,
                    }}
                    onClick={() =>
                        trackEvent({
                            category: 'product',
                            action: 'item tile browse similar click',
                        })
                    }
                >
                    <FormattedMessage
                        id="dc.buyerProductTile.browseSimilar"
                        defaultMessage="Browse Similar"
                    />
                </SeoLink>
            )}
        </p>
    );
};
